import { useCallback, useEffect, useRef, useState } from 'react';
import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import { isAbortError } from 'common/modules/media/isAbortError';
import {
  isVideoErrorRecoverable,
  MEDIA_ERR_SRC_NOT_SUPPORTED
} from '../utils/videoError';

/** A failure of the current source: one nullable field, so nothing can disagree. */
export interface IVideoLoadError {
  /** `MediaError.code`, or `null` when the element exposed no code. */
  code: number | null;
}

interface IVideoPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  buffered: number;
  playbackRate: number;
  isReady: boolean;
  hasStarted: boolean;
  hasEnded: boolean;
  error: IVideoLoadError | null;
  canRetry: boolean;
  /** True once the CORS fallback engaged: render without `crossOrigin` and `<track>`. */
  corsFallbackActive: boolean;
}

interface IVideoPlayerActions {
  togglePlay: () => void;
  seek: (time: number) => void;
  setPlaybackRate: (rate: number) => void;
  seekRelative: (delta: number) => void;
  retry: () => void;
}

interface IUseVideoPlayerOptions {
  src: string;
  /** Whether a `<track>` (and therefore `crossOrigin`) is in play. */
  hasSubtitles: boolean;
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
  onReady: () => void;
  onError: (error?: MediaError | null) => void;
}

// Retries per source. A deterministically broken source answers identically every time;
// capped so the student is never left tapping a button that cannot work.
const MAX_RETRY_ATTEMPTS = 2;

export default function useVideoPlayer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  options: IUseVideoPlayerOptions
): [IVideoPlayerState, IVideoPlayerActions] {
  const { playing, src, hasSubtitles } = options;

  // Refs, not deps: VideoContent passes non-memoized inline callbacks, so real deps
  // would re-register every listener on every render.
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [isReady, setIsReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [error, setError] = useState<IVideoLoadError | null>(null);
  const [corsFallbackActive, setCorsFallbackActive] = useState(false);
  // Mirrored in a ref so `retry()` can guard synchronously; the state copy drives the
  // re-render that hides an exhausted retry button.
  const [retryAttempts, setRetryAttempts] = useState(0);
  const retryAttemptsRef = useRef(0);
  // Bumped by `retry()`. `video.load()` has to run *after* React has committed the
  // `crossOrigin`/`<track>` changes the fallback implies — hence an effect, not the
  // click handler.
  const [reloadToken, setReloadToken] = useState(0);

  const animFrameRef = useRef<number>(0);
  const isPlayingRef = useRef(false);
  // Read inside stable callbacks so their identity never depends on the src.
  const srcRef = useRef(src);
  srcRef.current = src;
  const errorRef = useRef<IVideoLoadError | null>(null);
  errorRef.current = error;
  const corsFallbackActiveRef = useRef(false);
  corsFallbackActiveRef.current = corsFallbackActive;

  const reportPlayRejection = useCallback((rejection: unknown) => {
    // A `pause()` or src change while a `play()` promise is pending rejects with
    // AbortError — routine, and must not spend the one-report-per-src budget.
    if (isAbortError(rejection)) return;

    captureMediaLoadError(rejection, {
      kind: 'video',
      src: srcRef.current,
      context: 'play-rejected'
    });
  }, []);

  // Intentionally not gated on `isReady`: play() is valid before metadata arrives, so
  // gating on readiness only made a programmatic `playing` unreachable.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (playing && video.paused) {
      video.play().catch(reportPlayRejection);
    } else if (!playing && !video.paused) {
      video.pause();
    }
  }, [playing, videoRef, reportPlayRejection]);

  const startTimeUpdate = useCallback(() => {
    const update = () => {
      const video = videoRef.current;
      if (video) {
        setCurrentTime(video.currentTime);

        if (video.buffered.length > 0) {
          setBuffered(video.buffered.end(video.buffered.length - 1));
        }
      }

      if (isPlayingRef.current) {
        animFrameRef.current = requestAnimationFrame(update);
      }
    };

    animFrameRef.current = requestAnimationFrame(update);
  }, [videoRef]);

  const stopTimeUpdate = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
  }, []);

  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  // The listener effect below is registered once for the element's lifetime, so nothing
  // else clears state derived from the previous source. A stale `hasStarted` hid the
  // BigPlayButton, leaving the next video of a sequence with no way to start.
  const previousSrcRef = useRef(src);
  useEffect(() => {
    if (previousSrcRef.current === src) return;
    previousSrcRef.current = src;

    isPlayingRef.current = false;
    stopTimeUpdate();
    setIsPlaying(false);
    setIsReady(false);
    setHasStarted(false);
    setHasEnded(false);
    setCurrentTime(0);
    setBuffered(0);
    setDuration(0);
    setError(null);
    setCorsFallbackActive(false);
    retryAttemptsRef.current = 0;
    setRetryAttempts(0);
  }, [src, stopTimeUpdate]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsReady(true);
      // Metadata arrived, so the current source is playable — drop any stale error.
      setError(null);
      callbacksRef.current.onReady();
    };

    const handlePlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      setHasStarted(true);
      setHasEnded(false);
      startTimeUpdate();
      callbacksRef.current.onPlay();
    };

    const handlePause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      stopTimeUpdate();
      callbacksRef.current.onPause();
    };

    const handleEnded = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      setHasEnded(true);
      stopTimeUpdate();
      callbacksRef.current.onEnded();
    };

    const handleError = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      stopTimeUpdate();
      // Always a non-null object: `video.error` is null in some environments, and a
      // null here would read as "no error".
      setError({ code: video.error ? video.error.code : null });
      callbacksRef.current.onError(video.error);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    // The volume UI is desktop-only, so a muted element would be unrecoverable on touch.
    video.muted = false;
    video.volume = 1;

    // Catch-up: the element can already have loaded metadata (or failed)
    // before these listeners were attached — those events will not fire again.
    if (video.readyState >= 1) {
      handleLoadedMetadata();
    }
    if (video.error) {
      handleError();
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [videoRef, startTimeUpdate, stopTimeUpdate]);

  useEffect(() => {
    if (reloadToken === 0) return;

    videoRef.current?.load();
  }, [reloadToken, videoRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(reportPlayRejection);
    } else {
      video.pause();
    }
  }, [videoRef, reportPlayRejection]);

  const corsFallbackAvailable = hasSubtitles && !corsFallbackActive;
  const canRetry =
    error !== null &&
    retryAttempts < MAX_RETRY_ATTEMPTS &&
    isVideoErrorRecoverable(error.code, corsFallbackAvailable);

  const retry = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // Guarded off the ref so two fast taps cannot both get through.
    if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) return;

    retryAttemptsRef.current += 1;
    setRetryAttempts(retryAttemptsRef.current);

    /*
     * A plain `load()` re-issues the identical request. The likeliest native failure is
     * the CDN rejecting the CORS-mode media fetch that `crossOrigin="anonymous"` forces
     * whenever subtitles exist, so spend the first attempt dropping that attribute (and
     * the `<track>` that requires it); anything else gets a plain reload.
     */
    if (
      errorRef.current?.code === MEDIA_ERR_SRC_NOT_SUPPORTED &&
      !corsFallbackActiveRef.current
    ) {
      setCorsFallbackActive(true);
    }

    setError(null);
    setIsReady(false);
    // load() rewinds to the start: without resetting `hasStarted` the BigPlayButton —
    // the only gesture target that can legally start playback after a reload — would
    // stay hidden.
    setHasStarted(false);
    setHasEnded(false);
    setCurrentTime(0);
    setBuffered(0);
    setReloadToken((token) => token + 1);
  }, [videoRef]);

  const seek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(0, Math.min(time, video.duration || 0));
    },
    [videoRef]
  );

  const seekRelative = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(
        0,
        Math.min(video.currentTime + delta, video.duration || 0)
      );
    },
    [videoRef]
  );

  const setPlaybackRate = useCallback(
    (rate: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.playbackRate = rate;
      setPlaybackRateState(rate);
    },
    [videoRef]
  );

  return [
    {
      isPlaying,
      currentTime,
      duration,
      buffered,
      playbackRate,
      isReady,
      hasStarted,
      hasEnded,
      error,
      canRetry,
      corsFallbackActive
    },
    { togglePlay, seek, setPlaybackRate, seekRelative, retry }
  ];
}
